// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Wraps `GET /api/v1/users` (`ENDPOINTS.USERS` in `utils/constants.ts`) —
 * `/api/v1/admin/all-users` is deprecated, this replaces it. Lives in
 * `shared/` so admin-center never imports `services/`/`utils/` directly
 * (ADR-0014). The fetch wrapper itself (`get`) stays imported from the real
 * `services/api` — it carries token attach + 401/session-expiry handling
 * that must stay single-sourced, not duplicated.
 *
 * Each user carries `roles` and site access (`all_sites`, `sites`) that the
 * BFF resolves from IAM role assignments; `access_resolved: false` marks a row
 * whose lookup failed.
 */

import { del, get, post, put } from '../../helios/services/api';
import { ENDPOINTS } from '../utils/constants';
import type { FederatedInviteResponse, PasswordResetResponse, UserWithOrg } from '../types/user';

export interface InviteUserParams {
  email: string;
  firstName: string;
  lastName: string;
}

interface GetAllUsersParams {
  skip?: number;
  limit?: number;
  tenantId?: string;
  activeOnly?: boolean;
}

export const getAllUsers = async (params: GetAllUsersParams = {}): Promise<UserWithOrg[]> => {
  const queryParams = new URLSearchParams();
  if (params.skip !== undefined) queryParams.append('skip', String(params.skip));
  if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
  if (params.tenantId) queryParams.append('tenant_id', params.tenantId);
  if (params.activeOnly !== undefined) queryParams.append('active_only', String(params.activeOnly));

  const url = queryParams.toString() ? `${ENDPOINTS.USERS}?${queryParams}` : ENDPOINTS.USERS;
  return get<UserWithOrg[]>(url);
};

/** `GET /api/v1/users/{id}` — one user, with roles/site access attached (ADR-0026). */
export const getUserById = async (userId: string): Promise<UserWithOrg> => {
  return get<UserWithOrg>(ENDPOINTS.USER_BY_ID(userId));
};

/**
 * `POST /api/v1/users/invite` — creates the IAM user. `UserInvite` has no
 * role field (ADR-0026) and its `site_ids` is unused by the BFF today, so
 * role and site access are granted afterward as their own
 * `roleAssignmentService.createRoleAssignment` calls against the id this
 * returns — the same two-step pattern the edit screen uses (ADO #22696).
 */
export const inviteUser = async (params: InviteUserParams): Promise<UserWithOrg> => {
  return post<UserWithOrg>(ENDPOINTS.USERS_INVITE, {
    email: params.email,
    first_name: params.firstName,
    last_name: params.lastName,
  });
};

export interface InviteFederatedUserParams {
  email: string;
  /** Optional: IAM falls back to the email until their first sign-in. */
  displayName?: string;
}

/**
 * `POST /api/v1/users/federated/invite` — pre-provisions an employee who signs
 * in with their company account. `existing` means a federated user with that
 * email is already in the organization: assigning the chosen role is still
 * correct, and is how a retry completes after the assignment step failed.
 * Refusals reject with an `ApiError` carrying the BFF `code`.
 */
export const inviteFederatedUser = async (
  params: InviteFederatedUserParams
): Promise<FederatedInviteResponse> => {
  return post<FederatedInviteResponse>(ENDPOINTS.USERS_FEDERATED_INVITE, {
    email: params.email,
    ...(params.displayName ? { display_name: params.displayName } : {}),
  });
};

/** `GET /api/v1/users/federated/domains` — this organization's company sign-in domains. */
export const getFederatedDomains = async (): Promise<string[]> => {
  const response = await get<{ email_domains: string[] }>(ENDPOINTS.USERS_FEDERATED_DOMAINS);
  return response.email_domains;
};

/**
 * `POST /api/v1/users/{id}/reset-password` — replaces a local user's password
 * with a temporary one and signs them out everywhere. The result is a live
 * credential: show it once and never cache it.
 */
export const resetUserPassword = async (userId: string): Promise<PasswordResetResponse> => {
  return post<PasswordResetResponse>(ENDPOINTS.USER_RESET_PASSWORD(userId));
};

/**
 * `PUT /api/v1/users/{id}` with only `status` set — activates or deactivates
 * an account. `UserUpdate` no longer carries a role field (ADR-0026): role
 * changes go through `roleAssignmentService`, not this call.
 */
export const updateUserStatus = async (
  userId: string,
  status: 'active' | 'inactive'
): Promise<UserWithOrg> => {
  return put<UserWithOrg>(ENDPOINTS.USER_BY_ID(userId), { status });
};

/**
 * Wraps `DELETE /api/v1/users/{userId}`. The BFF soft-deletes via IAM and
 * answers 204 with no body on success (`back/app/routers/users.py`).
 */
export const deleteUser = async (userId: string): Promise<void> => {
  await del<void>(ENDPOINTS.USER_BY_ID(userId));
};
